
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { getAssessmentBySlug } from '@/services/assessmentService';
import { loadPremiumProgress, createPremiumAttempt, listOngoingAttemptsForAssessment, getNextAvailableAttemptName } from '@/services/premiumProgressService';
import { Assessment } from '@/types/assessment';
import { useToast } from '@/hooks/use-toast';
import Logo from '@/icons/Logo';
import PremiumDashboard from '@/components/premium-dashboard/PremiumDashboard';
import PremiumResumeDialog from '@/components/premium-dashboard/PremiumResumeDialog';
import PremiumAssessment from '@/pages/assessment/PremiumAssessment';
import { isQuestionFullyAnswered } from '@/utils/assessmentHelpers';

const hasLocalOngoingAssessment = (assessmentId: string): boolean => {
  try {
    const stored = localStorage.getItem(`assessment_${assessmentId}_responses`);
    if (!stored) return false;

    const storedProgress = localStorage.getItem(`assessment-progress-${assessmentId}`);
    if (storedProgress) {
      const parsedProgress = JSON.parse(storedProgress);
      // Treat 'complete' state as not ongoing — it's leftover from viewing past results
      if (parsedProgress?.step === 'complete') return false;
    }

    const parsed = JSON.parse(stored);
    return Object.keys(parsed).length > 0;
  } catch {
    return false;
  }
};

const clearLegacyAssessmentProgress = (assessmentId: string) => {
  localStorage.removeItem(`assessment_${assessmentId}_responses`);
  localStorage.removeItem(`assessment-progress-${assessmentId}`);
  localStorage.removeItem(`assessment-progress-premium-${assessmentId}`);
};

const PremiumPortal = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [premiumView, setPremiumView] = useState<'dashboard' | 'assessment'>('dashboard');
  const [selectedAssessment, setSelectedAssessment] = useState<Assessment | null>(null);
  const [loadingSelected, setLoadingSelected] = useState(false);
  const [startFresh, setStartFresh] = useState(false);
  const [attemptId, setAttemptId] = useState<string>('');
  const [initialCustomName, setInitialCustomName] = useState<string | null>(null);
  const [resumeDialogOpen, setResumeDialogOpen] = useState(false);
  const [pendingAssessment, setPendingAssessment] = useState<Assessment | null>(null);
  const [pendingProgressInfo, setPendingProgressInfo] = useState<{ percentage: number; updatedAt: string | null }>({ percentage: 0, updatedAt: null });
  // Tracks the assessment id being viewed in "view past results" mode so we can clean up
  // its localStorage leftovers when the user returns to the dashboard.
  const viewingResultsAssessmentId = useRef<string | null>(null);
  const { toast } = useToast();

  // Auth check
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        navigate('/', { replace: true });
        return;
      }

      const { data: orgMember } = await supabase
        .from('organization_members')
        .select('id')
        .eq('id', session.user.id)
        .maybeSingle();

      if (!orgMember) {
        navigate('/', { replace: true });
        return;
      }

      setLoading(false);
    };

    checkAuth();
  }, [navigate]);

  // Handle browser back button
  useEffect(() => {
    const handlePopState = () => {
      if (premiumView === 'assessment') {
        const viewedId = viewingResultsAssessmentId.current;
        if (viewedId) {
          localStorage.removeItem(`assessment_${viewedId}_responses`);
          localStorage.removeItem(`assessment-progress-${viewedId}`);
          viewingResultsAssessmentId.current = null;
        }
        setPremiumView('dashboard');
        setSelectedAssessment(null);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [premiumView]);

  const launchAssessment = async (data: Assessment, fresh: boolean, preferredAttemptId?: string) => {
    let id: string | null = null;
    let seededName: string | null = null;
    if (fresh) {
      clearLegacyAssessmentProgress(data.id);
      seededName = await getNextAvailableAttemptName(data.title);
      id = await createPremiumAttempt(data.id, seededName);
    } else {
      id = preferredAttemptId ?? null;
      const attempts = await listOngoingAttemptsForAssessment(data.id);
      const selectedAttempt = preferredAttemptId
        ? attempts.find((attempt) => attempt.id === preferredAttemptId) ?? null
        : attempts[0] ?? null;
      if (!id) {
        id = selectedAttempt?.id ?? null;
      }
      seededName = selectedAttempt?.custom_name ?? null;
      id = id ?? (await createPremiumAttempt(data.id, null));
    }
    if (!id) {
      toast({ variant: "destructive", title: "Error", description: "Could not start the assessment. Please try again." });
      return;
    }
    setStartFresh(fresh);
    setAttemptId(id);
    setInitialCustomName(seededName);
    setSelectedAssessment(data);
    setPremiumView('assessment');
    window.history.pushState({ view: 'assessment' }, '');
  };

  const handleStartPremiumAssessment = async (assessmentId: string, assessmentSlug: string) => {
    setLoadingSelected(true);
    try {
      // Source of truth for "real" ongoing progress is the DB (premium_assessment_progress).
      // localStorage may contain leftover data from a previous "View Results" click.
      let dbProgress = null;
      try {
        dbProgress = await loadPremiumProgress(assessmentId);
      } catch (err) {
        console.warn('Could not check DB progress:', err);
      }

      const hasRealOngoing =
        !!dbProgress &&
        !!dbProgress.responses &&
        Object.keys(dbProgress.responses).length > 0;

      const data = await getAssessmentBySlug(assessmentSlug);
      if (!data) {
        toast({ variant: "destructive", title: "Error", description: "Could not load the selected assessment." });
        return;
      }

      if (hasRealOngoing) {
        // Restore DB progress into localStorage so resume works, then prompt
        localStorage.setItem(
          `assessment_${assessmentId}_responses`,
          JSON.stringify(dbProgress!.responses)
        );
        // Make sure no stale 'complete' step is left from a prior View Results
        localStorage.removeItem(`assessment-progress-${assessmentId}`);

        // Compute completion percentage across ALL questions in the assessment
        const allQuestions = (data.categories || []).flatMap((c: any) =>
          (c.questions || []).filter((q: any) => !q.disabled)
        );
        const totalQuestions = allQuestions.length;
        const responses = dbProgress!.responses || {};
        const completedCount = allQuestions.filter((q: any) =>
          isQuestionFullyAnswered(responses[q.id])
        ).length;
        const percentage = totalQuestions > 0
          ? Math.round((completedCount / totalQuestions) * 100)
          : 0;

        setPendingAssessment(data);
        setPendingProgressInfo({ percentage, updatedAt: dbProgress!.updated_at || null });
        setResumeDialogOpen(true);
        return;
      }

      // No real ongoing progress → wipe any stale local/DB state and start fresh.
      // Completed attempts remain visible only via "My Assessments → View Results".
       clearLegacyAssessmentProgress(assessmentId);
       launchAssessment(data, true);
    } catch {
      toast({ variant: "destructive", title: "Error", description: "Failed to load assessment." });
    } finally {
      setLoadingSelected(false);
    }
  };

  const handleViewResults = async (assessmentId: string, assessmentSlug: string, responseId?: string) => {
    setLoadingSelected(true);
    try {
      const data = await getAssessmentBySlug(assessmentSlug);
      if (!data) {
        toast({ variant: "destructive", title: "Error", description: "Could not load the assessment." });
        return;
      }

      // If we have a responseId, load that specific response's answers into localStorage
      // so the results page shows the correct data for this attempt
      if (responseId) {
        try {
          const { data: myResponses } = await supabase.functions.invoke('get-my-responses');
          const allResponses = Array.isArray(myResponses) ? myResponses : [];
          const matchedResponse = allResponses.find((r: any) => r.id === responseId);
          
          if (matchedResponse?.answerDetails) {
            // Import and use the prefill function to reconstruct localStorage-compatible responses
            const { buildPrefilledResponsesFromAnswerDetails } = await import('./assessment/premiumAssessmentPrefill');
            const sortedCategories = data.categories
              .filter((c: any) => c.questions.some((q: any) => !q.disabled))
              .map((c: any) => ({
                ...c,
                questions: c.questions.filter((q: any) => !q.disabled),
              }));
            
            const reconstructed = buildPrefilledResponsesFromAnswerDetails(
              matchedResponse.answerDetails,
              sortedCategories
            );
            localStorage.setItem(`assessment_${assessmentId}_responses`, JSON.stringify(reconstructed));
          }
        } catch (err) {
          console.warn('Could not load specific response data:', err);
        }
      }

      // Force progress to 'complete' so the results page renders
      const progressKey = `assessment-progress-${assessmentId}`;
      const existing = localStorage.getItem(progressKey);
      const parsed = existing ? JSON.parse(existing) : {};
      localStorage.setItem(progressKey, JSON.stringify({ ...parsed, step: 'complete' }));
      setStartFresh(false);
      setAttemptId('');
      setInitialCustomName(null);
      setSelectedAssessment(data);
      setPremiumView('assessment');
      viewingResultsAssessmentId.current = assessmentId;
      window.history.pushState({ view: 'assessment' }, '');
    } catch {
      toast({ variant: "destructive", title: "Error", description: "Failed to load assessment." });
    } finally {
      setLoadingSelected(false);
    }
  };

  const handleContinueAssessment = async (assessmentId: string, assessmentSlug: string, preferredAttemptId?: string) => {
    const localStored = preferredAttemptId
      ? localStorage.getItem(`assessment_${assessmentId}_attempt_${preferredAttemptId}_responses`)
      : localStorage.getItem(`assessment_${assessmentId}_responses`);
    if (!localStored || Object.keys(JSON.parse(localStored || '{}')).length === 0) {
      try {
        const dbProgress = preferredAttemptId
          ? (await listOngoingAttemptsForAssessment(assessmentId)).find((attempt) => attempt.id === preferredAttemptId) ?? null
          : await loadPremiumProgress(assessmentId);
        if (dbProgress?.responses && Object.keys(dbProgress.responses).length > 0) {
          localStorage.setItem(`assessment_${assessmentId}_attempt_${dbProgress.id}_responses`, JSON.stringify(dbProgress.responses));
        }
      } catch {}
    }

    setLoadingSelected(true);
    try {
      const data = await getAssessmentBySlug(assessmentSlug);
      if (data) {
        launchAssessment(data, false, preferredAttemptId);
      } else {
        toast({ variant: "destructive", title: "Error", description: "Could not load the selected assessment." });
      }
    } catch {
      toast({ variant: "destructive", title: "Error", description: "Failed to load assessment." });
    } finally {
      setLoadingSelected(false);
    }
  };

  const handleResumeChoice = (resume: boolean) => {
    setResumeDialogOpen(false);
    if (pendingAssessment) {
      launchAssessment(pendingAssessment, !resume);
      setPendingAssessment(null);
    }
  };

  const handleBackToDashboard = () => {
    // If user was viewing past results, clean up the localStorage entries we wrote
    // so the next "Start Premium Assessment" begins from a clean slate.
    const viewedId = viewingResultsAssessmentId.current;
    if (viewedId) {
      localStorage.removeItem(`assessment_${viewedId}_responses`);
      localStorage.removeItem(`assessment-progress-${viewedId}`);
      viewingResultsAssessmentId.current = null;
    }
    setPremiumView('dashboard');
    setSelectedAssessment(null);
    window.history.back();
  };

  const handleLogout = () => {
    supabase.auth.signOut();
    navigate('/', { replace: true });
  };

  if (loading || loadingSelected) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-muted/30">
        <div className="flex items-center gap-3">
          <Logo className="h-8 w-8" />
          <span className="text-lg text-foreground">Loading...</span>
        </div>
      </div>
    );
  }

  if (premiumView === 'assessment' && selectedAssessment) {
    return (
      <PremiumAssessment
        assessment={selectedAssessment}
        onBack={handleBackToDashboard}
        attemptId={attemptId}
        isFreshAttempt={startFresh}
          initialCustomName={initialCustomName}
      />
    );
  }

  return (
    <>
      <PremiumDashboard
        onStartAssessment={handleStartPremiumAssessment}
        onContinueAssessment={handleContinueAssessment}
        onViewResults={handleViewResults}
        onLogout={handleLogout}
      />
      <PremiumResumeDialog
        open={resumeDialogOpen}
        onOpenChange={setResumeDialogOpen}
        assessmentTitle={pendingAssessment?.title || ''}
        completionPercentage={pendingProgressInfo.percentage}
        lastUpdatedAt={pendingProgressInfo.updatedAt}
        onResume={() => handleResumeChoice(true)}
        onStartNew={() => handleResumeChoice(false)}
      />
    </>
  );
};

export default PremiumPortal;
